// Life & Wealth Solutions — Lead Management V34
// Works fully without third-party services configured

import React, { useState, useEffect } from 'react';

type LeadStatus = 'New' | 'Contacted' | 'Qualified' | 'Quoted' | 'Closed' | 'Lost';
type PolicyType = 'Life Insurance' | 'Home Insurance' | 'Auto Insurance' | 'Commercial' | 'Health' | 'Other';

interface Lead {
  id: number;
  full_name: string;
  email: string;
  phone: string;
  state: string;
  policy_interest: PolicyType;
  status: LeadStatus;
  score: number;
  source: string;
  notes: string;
  created: string;
}

const MOCK_LEADS: Lead[] = [
  { id:1, full_name:'John Rivera', email:'john.r@email.com', phone:'(404) 555-0101', state:'GA', policy_interest:'Life Insurance', status:'Qualified', score:87, source:'Referral', notes:'Interested in whole life. Has 2 kids.', created:'2026-04-10' },
  { id:2, full_name:'Sarah Chen', email:'sarah.c@email.com', phone:'(770) 555-0102', state:'GA', policy_interest:'Home Insurance', status:'Quoted', score:72, source:'Web Form', notes:'First-time homeowner.', created:'2026-04-11' },
  { id:3, full_name:'Marcus Williams', email:'marcus.w@email.com', phone:'(678) 555-0103', state:'FL', policy_interest:'Commercial', status:'New', score:91, source:'Cold Outreach', notes:'Owns 3 restaurants. Whale candidate.', created:'2026-04-12' },
  { id:4, full_name:'Lisa Thompson', email:'lisa.t@email.com', phone:'(404) 555-0104', state:'TX', policy_interest:'Auto Insurance', status:'Contacted', score:55, source:'Web Form', notes:'Switching from GEICO.', created:'2026-04-12' },
  { id:5, full_name:'David Park', email:'david.p@email.com', phone:'(404) 555-0105', state:'GA', policy_interest:'Life Insurance', status:'New', score:68, source:'Referral', notes:'Referred by John Rivera.', created:'2026-04-13' },
];

const STATUS_COLORS: Record<LeadStatus, string> = {
  New: 'bg-blue-100 text-blue-700',
  Contacted: 'bg-yellow-100 text-yellow-700',
  Qualified: 'bg-purple-100 text-purple-700',
  Quoted: 'bg-orange-100 text-orange-700',
  Closed: 'bg-green-100 text-green-700',
  Lost: 'bg-red-100 text-red-700',
};

const STATUSES: LeadStatus[] = ['New', 'Contacted', 'Qualified', 'Quoted', 'Closed', 'Lost'];
const POLICIES: PolicyType[] = ['Life Insurance', 'Home Insurance', 'Auto Insurance', 'Commercial', 'Health', 'Other'];
const STATES = ['AL','AZ','CA','CO','FL','GA','IL','MI','NC','NJ','NY','OH','PA','TN','TX','VA','WA'];

const EMPTY_FORM = { full_name:'', email:'', phone:'', state:'GA', policy_interest:'Life Insurance' as PolicyType, source:'Web Form', notes:'' };

const LeadManagement: React.FC = () => {
  const [leads, setLeads] = useState<Lead[]>(() => {
    try {
      const stored = localStorage.getItem('lw_leads');
      if (stored) return JSON.parse(stored) as Lead[];
    } catch {}
    return MOCK_LEADS;
  });
  // Persist leads to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('lw_leads', JSON.stringify(leads));
    } catch {}
  }, [leads]);

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('All');
  const [filterState, setFilterState] = useState('All');
  const [showModal, setShowModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saved, setSaved] = useState(false);

  const filtered = leads.filter(l =>
    (filterStatus === 'All' || l.status === filterStatus) &&
    (filterState === 'All' || l.state === filterState) &&
    (l.full_name.toLowerCase().includes(search.toLowerCase()) ||
     l.email.toLowerCase().includes(search.toLowerCase()) ||
     l.policy_interest.toLowerCase().includes(search.toLowerCase()))
  );

  const openAdd = () => { setSelectedLead(null); setForm(EMPTY_FORM); setShowModal(true); };
  const openEdit = (lead: Lead) => {
    setSelectedLead(lead);
    setForm({ full_name: lead.full_name, email: lead.email, phone: lead.phone, state: lead.state, policy_interest: lead.policy_interest, source: lead.source, notes: lead.notes });
    setShowModal(true);
  };

  const save = () => {
    if (!form.full_name.trim()) return;
    if (selectedLead) {
      setLeads(p => p.map(l => l.id === selectedLead.id ? { ...l, ...form } : l));
    } else {
      setLeads(p => [...p, { id: Date.now(), ...form, status: 'New', score: Math.floor(50 + Math.random() * 40), created: new Date().toISOString().split('T')[0] }]);
    }
    setShowModal(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const updateStatus = (id: number, status: LeadStatus) =>
    setLeads(p => p.map(l => l.id === id ? { ...l, status } : l));

  const deleteLead = (id: number) => {
    if (window.confirm('Delete this lead?')) setLeads(p => p.filter(l => l.id !== id));
  };

  const scoreColor = (s: number) => s >= 80 ? 'text-green-600' : s >= 60 ? 'text-yellow-600' : 'text-red-500';

  return (
    <div className="space-y-5 pb-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leads ({filtered.length})</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {leads.filter(l => l.status === 'New').length} new ·
            {leads.filter(l => l.status === 'Qualified').length} qualified ·
            {leads.filter(l => l.status === 'Quoted').length} quoted
          </p>
        </div>
        <div className="flex gap-2">
          {saved && <span className="self-center text-sm text-green-600 font-medium">✓ Saved</span>}
          <button onClick={openAdd} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700">
            + New Lead
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search name, email, policy type..."
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none">
          <option value="All">All Statuses</option>
          {STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={filterState} onChange={e => setFilterState(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none">
          <option value="All">All States</option>
          {STATES.map(s => <option key={s}>{s}</option>)}
        </select>
      </div>

      {/* Lead Cards */}
      <div className="space-y-2">
        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <p className="text-gray-400 text-sm">No leads match your filters.</p>
            <button onClick={openAdd} className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium">
              + Add your first lead
            </button>
          </div>
        ) : filtered.map(lead => (
          <div key={lead.id} className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-sm transition-shadow">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-3 min-w-0 flex-1">
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm flex-shrink-0">
                  {lead.full_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-gray-900">{lead.full_name}</p>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[lead.status]}`}>{lead.status}</span>
                    <span className={`text-xs font-bold ${scoreColor(lead.score)}`}>Score: {lead.score}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{lead.email} · {lead.phone} · {lead.state}</p>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    <span className="text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded">{lead.policy_interest}</span>
                    <span className="text-xs text-gray-500">Source: {lead.source}</span>
                    <span className="text-xs text-gray-400">Added {lead.created}</span>
                  </div>
                  {lead.notes && <p className="text-xs text-gray-500 mt-1 italic">{lead.notes}</p>}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <select
                  value={lead.status}
                  onChange={e => updateStatus(lead.id, e.target.value as LeadStatus)}
                  className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                >
                  {STATUSES.map(s => <option key={s}>{s}</option>)}
                </select>
                <button onClick={() => openEdit(lead)} className="px-2.5 py-1.5 text-xs text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100">Edit</button>
                <button onClick={() => deleteLead(lead.id)} className="px-2.5 py-1.5 text-xs text-red-500 bg-red-50 rounded-lg hover:bg-red-100">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-2xl">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{selectedLead ? 'Edit Lead' : 'Add New Lead'}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Full Name *</label>
                <input value={form.full_name} onChange={e => setForm(p => ({ ...p, full_name: e.target.value }))}
                  placeholder="First and last name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="email@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                <input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                  placeholder="(404) 555-0100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">State</label>
                <select value={form.state} onChange={e => setForm(p => ({ ...p, state: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none">
                  {STATES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Policy Interest</label>
                <select value={form.policy_interest} onChange={e => setForm(p => ({ ...p, policy_interest: e.target.value as PolicyType }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none">
                  {POLICIES.map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Lead Source</label>
                <select value={form.source} onChange={e => setForm(p => ({ ...p, source: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none">
                  {['Web Form','Referral','Cold Outreach','Social Media','Phone Call','Walk-In','Other'].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Any relevant notes about this lead..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={save} className="flex-1 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700">
                {selectedLead ? 'Save Changes' : 'Add Lead'}
              </button>
              <button onClick={() => setShowModal(false)} className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadManagement;
